import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import BloomsTaxonomyHelper from './BloomsTaxonomyHelper';
import VocabularyTierHelper from './VocabularyTierHelper';
import CefrLevelHelper from './CefrLevelHelper';
import { storage } from '../../data/localStorage';
import { cloService } from '../../services/cloService';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const { FiX, FiPlus, FiMinus, FiBookOpen, FiTarget } = FiIcons;

const AddQuestionModal = ({ isOpen, onClose, onSubmit }) => {
  const { currentWorkspace } = useWorkspace();
  const [formData, setFormData] = useState({
    text: '',
    type: 'Multiple Choice',
    subject: '',
    courseCode: '',
    cloId: '',
    difficulty: 'Medium',
    bloomLevel: '',
    vocabularyTier: '',
    cefrLevel: '', // New CEFR level field
    marks: 1,
    options: ['', '', '', ''],
    correctAnswer: ''
  });

  const [availableCLOs, setAvailableCLOs] = useState([]);
  const [courseCodes, setCourseCodes] = useState([]);
  const [filteredCLOs, setFilteredCLOs] = useState([]);

  const questionTypes = [
    'Multiple Choice',
    'Short Answer',
    'Essay',
    'True/False',
    'Fill in the Blank',
    'Problem Solving'
  ];

  const difficulties = ['Easy', 'Medium', 'Hard'];

  useEffect(() => {
    if (isOpen) {
      loadCLOs();
    }
  }, [isOpen, currentWorkspace?.id]);

  useEffect(() => {
    // Filter CLOs based on selected course code
    if (formData.courseCode) {
      const filtered = availableCLOs.filter(clo => clo.courseCode === formData.courseCode);
      setFilteredCLOs(filtered);
      // Reset CLO selection if current selection doesn't match course
      if (formData.cloId && !filtered.find(clo => clo.id === formData.cloId)) {
        setFormData(prev => ({ ...prev, cloId: '' }));
      }
    } else {
      setFilteredCLOs([]);
      setFormData(prev => ({ ...prev, cloId: '' }));
    }
  }, [formData.courseCode, availableCLOs]);

  const loadCLOs = async () => {
    try {
      let clos;
      if (currentWorkspace?.id) {
        clos = await cloService.getCLOs(currentWorkspace.id);
      } else {
        clos = storage.getCLOs();
      }
      setAvailableCLOs(clos);
      // Extract unique course codes
      const codes = [...new Set(clos.map(clo => clo.courseCode))];
      setCourseCodes(codes);
    } catch (error) {
      console.error('Error loading CLOs:', error);
      const clos = storage.getCLOs();
      setAvailableCLOs(clos);
      const codes = [...new Set(clos.map(clo => clo.courseCode))];
      setCourseCodes(codes);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.text.trim() || !formData.subject.trim() || !formData.courseCode || !formData.cloId || 
        !formData.bloomLevel || !formData.vocabularyTier || !formData.cefrLevel) {
      alert('Please fill in all required fields including Course Code, CLO and language levels');
      return;
    }
    onSubmit(formData);
    setFormData({
      text: '',
      type: 'Multiple Choice',
      subject: '',
      courseCode: '',
      cloId: '',
      difficulty: 'Medium',
      bloomLevel: '',
      vocabularyTier: '',
      cefrLevel: '',
      marks: 1,
      options: ['', '', '', ''],
      correctAnswer: ''
    });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const addOption = () => {
    setFormData({ ...formData, options: [...formData.options, ''] });
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData({ ...formData, options: newOptions });
    }
  };

  const getSelectedCLO = () => {
    return availableCLOs.find(clo => clo.id === formData.cloId);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Add New Question</h3>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Question Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Text *
                </label>
                <textarea
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your question... (e.g., 'Apply the principles of...' for Application level)"
                  required
                />
              </div>

              {/* Course Code and CLO Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Code *
                  </label>
                  <select
                    value={formData.courseCode}
                    onChange={(e) => setFormData({ ...formData, courseCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Course Code</option>
                    {courseCodes.map(code => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </select>
                  {courseCodes.length === 0 && (
                    <p className="text-xs text-orange-600 mt-1">
                      No course codes available. Please create CLOs first.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Learning Outcome (CLO) *
                  </label>
                  <select
                    value={formData.cloId}
                    onChange={(e) => setFormData({ ...formData, cloId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={!formData.courseCode}
                    required
                  >
                    <option value="">Select CLO</option>
                    {filteredCLOs.map(clo => (
                      <option key={clo.id} value={clo.id}>
                        {clo.cloNumber}: {clo.description.substring(0, 60)}...
                      </option>
                    ))}
                  </select>
                  {formData.courseCode && filteredCLOs.length === 0 && (
                    <p className="text-xs text-orange-600 mt-1">
                      No CLOs available for this course. Please create CLOs first.
                    </p>
                  )}
                </div>
              </div>

              {/* Selected CLO Display */}
              {getSelectedCLO() && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                    <SafeIcon icon={FiTarget} className="mr-2" />
                    Selected CLO: {getSelectedCLO().cloNumber}
                  </h4>
                  <p className="text-sm text-blue-700">{getSelectedCLO().description}</p>
                </div>
              )}

              {/* Analysis Helpers */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bloom's Taxonomy */}
                <div>
                  <BloomsTaxonomyHelper
                    selectedLevel={formData.bloomLevel}
                    onLevelChange={(level) => setFormData({ ...formData, bloomLevel: level })}
                    questionText={formData.text}
                    showAlignment={formData.text.length > 10}
                  />
                </div>

                {/* Vocabulary Tier */}
                <div>
                  <VocabularyTierHelper
                    selectedTier={formData.vocabularyTier}
                    onTierChange={(tier) => setFormData({ ...formData, vocabularyTier: tier })}
                    questionText={formData.text}
                    questionType={formData.type}
                    showAnalysis={formData.text.length > 10}
                  />
                </div>
              </div>

              {/* CEFR Language Level */}
              <div>
                <CefrLevelHelper
                  selectedLevel={formData.cefrLevel}
                  onLevelChange={(level) => setFormData({ ...formData, cefrLevel: level })}
                  questionText={formData.text}
                  showAnalysis={formData.text.length > 10}
                />
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., Mathematics"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {questionTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulty
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {difficulties.map(difficulty => (
                      <option key={difficulty} value={difficulty}>{difficulty}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marks
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.marks}
                    onChange={(e) => setFormData({ ...formData, marks: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Options for Multiple Choice */}
              {formData.type === 'Multiple Choice' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Options
                  </label>
                  <div className="space-y-2">
                    {formData.options.map((option, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => handleOptionChange(index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder={`Option ${index + 1}`}
                        />
                        {formData.options.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeOption(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <SafeIcon icon={FiMinus} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addOption}
                      className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      <SafeIcon icon={FiPlus} />
                      <span>Add Option</span>
                    </button>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Correct Answer
                    </label>
                    <select
                      value={formData.correctAnswer}
                      onChange={(e) => setFormData({ ...formData, correctAnswer: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select correct answer</option>
                      {formData.options.filter(opt => opt.trim()).map((option, index) => (
                        <option key={index} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <motion.button
                  type="submit"
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Add Question
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddQuestionModal;